import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Search, ScanBarcode, X, Check, AlertCircle, FileText, Receipt } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

// ══════════════════════════════════════════════════════════════════════════════
// BARCODE SCANNER MODAL
// ══════════════════════════════════════════════════════════════════════════════
const BarcodeScannerModal = ({ isOpen, onClose, onScan, products }) => {
  const [barcode, setBarcode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const handleScan = () => {
    if (!barcode.trim()) return;
    const product = products.find(p => 
      p.code?.toLowerCase() === barcode.toLowerCase() ||
      p.barcode?.toLowerCase() === barcode.toLowerCase() ||
      p.sku?.toLowerCase() === barcode.toLowerCase()
    );
    if (product) {
      onScan(product);
      setSuccess(`Added: ${product.name}`);
      setError('');
      setBarcode('');
      setTimeout(() => setSuccess(''), 2000);
      inputRef.current?.focus();
    } else {
      setError('Item not found. Try again.');
      setSuccess('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <ScanBarcode className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-slate-800">Scan Item</h3>
              <p className="text-[12px] text-slate-500">Scan barcode or type manually</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-6">
          <div className="relative">
            <input ref={inputRef} type="text" value={barcode}
              onChange={(e) => { setBarcode(e.target.value); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleScan()}
              className="w-full px-4 py-3 pr-12 bg-slate-50 border border-slate-200 rounded-xl text-[14px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none"
              placeholder="Scan barcode or type and press Enter" autoFocus
            />
            <button onClick={handleScan} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Check className="w-4 h-4" />
            </button>
          </div>
          {success && (
            <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-[13px] text-emerald-700 font-medium">{success}</span>
            </div>
          )}
          {error && (
            <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-[13px] text-red-700">{error}</span>
            </div>
          )}
          <p className="mt-4 text-[12px] text-slate-500 text-center">Modal stays open for rapid scanning. Press Enter after each scan.</p>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN CREATE PROFORMA INVOICE PAGE
// ══════════════════════════════════════════════════════════════════════════════
const CreateProformaInvoice = () => {
  const { businessId, id: proformaId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const isEditing = !!proformaId;
  const bid = businessId || activeBusiness?.id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const customerSearchRef = useRef(null);
  const itemSearchRef = useRef(null);

  const [formData, setFormData] = useState({
    customer_id: '',
    proforma_date: new Date().toISOString().split('T')[0],
    expiry_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    payment_terms: 'net_30',
    place_of_supply: '',
    reference_number: '',
    salesperson: '',
    delivery_method: '',
    items: [],
    shipping_charges: 0,
    adjustment: 0,
    customer_notes: '',
    terms_and_conditions: '',
    enable_round_off: true
  });

  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    if (bid) {
      fetchCustomers();
      fetchProducts();
      if (isEditing) fetchProforma();
    }
  }, [bid, proformaId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerSearchRef.current && !customerSearchRef.current.contains(e.target)) {
        setShowCustomerDropdown(false);
      }
      if (itemSearchRef.current && !itemSearchRef.current.contains(e.target)) {
        setShowItemDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProforma = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/proforma-invoices/${proformaId}`);
      const data = response.data;
      setFormData(prev => ({
          ...prev,
          customer_id: data.party_id || data.customer_id || '',
          proforma_date: data.proforma_date || prev.proforma_date,
          expiry_date: data.expiry_date || prev.expiry_date,
          payment_terms: data.payment_terms || 'net_30',
          place_of_supply: data.place_of_supply || '',
          reference_number: data.reference_number || '',
          salesperson: data.salesperson || '',
          delivery_method: data.delivery_method || '',
          items: data.items || [],
          shipping_charges: data.shipping_charges || 0,
          adjustment: data.adjustment || 0,
          customer_notes: data.customer_notes || '',
          terms_and_conditions: data.terms_and_conditions || '',
          enable_round_off: data.enable_round_off ?? true
        }));
        setCustomerSearch(data.party_name || data.customer_name || '');
    } catch (error) { toast.error(getApiErrorMessage(error, 'Failed to load proforma')); }
    finally { setLoading(false); }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/parties?type=customer');
      const data = response.data;
      setCustomers(data.parties || data || []);
    } catch (error) { toast.error(getApiErrorMessage(error, 'Failed to load customers')); }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/items');
      const data = response.data;
      setProducts(data.data || data || []);
    } catch (error) { toast.error(getApiErrorMessage(error, 'Failed to load products')); }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
    setFormData(prev => ({ ...prev, customer_id: customer.party_id || customer.id }));
  };

  const addItem = (product = null) => {
    if (product) {
      const existingIndex = formData.items.findIndex(item => item.item_id === product.id);
      if (existingIndex >= 0) {
        updateItem(existingIndex, 'quantity', (formData.items[existingIndex].quantity || 1) + 1);
        return;
      }
      const lineTotal = 1 * (product.selling_price || product.sale_price || 0);
      const taxRate = product.tax_rate || 0;
      const taxAmt = lineTotal * (taxRate / 100);
      
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          item_id: product.id, item_name: product.name, item_code: product.code || '',
          hsn_code: product.hsn_code || '', quantity: 1, unit: product.unit || 'Nos',
          rate: product.selling_price || product.sale_price || 0,
          discount_percent: 0, discount_amount: 0, tax_rate: taxRate,
          tax_amount: Math.round(taxAmt * 100) / 100, amount: Math.round((lineTotal + taxAmt) * 100) / 100
        }]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          item_id: '', item_name: '', item_code: '', hsn_code: '', quantity: 1, unit: 'Nos',
          rate: 0, discount_percent: 0, discount_amount: 0, tax_rate: 0, tax_amount: 0, amount: 0
        }]
      }));
    }
    setItemSearch('');
    setShowItemDropdown(false);
  };

  const removeItem = (index) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      if (field === 'item_id') {
        const product = products.find(p => p.id === value);
        if (product) {
          newItems[index].item_name = product.name;
          newItems[index].item_code = product.code || '';
          newItems[index].hsn_code = product.hsn_code || '';
          newItems[index].rate = product.selling_price || product.sale_price || 0;
          newItems[index].unit = product.unit || 'Nos';
          newItems[index].tax_rate = product.tax_rate || 0;
        }
      }

      const qty = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      const discountPercent = parseFloat(newItems[index].discount_percent) || 0;
      const taxRate = parseFloat(newItems[index].tax_rate) || 0;

      const lineTotal = qty * rate;
      const discountAmt = lineTotal * (discountPercent / 100);
      const taxableAmount = lineTotal - discountAmt;
      const taxAmt = taxableAmount * (taxRate / 100);

      newItems[index].discount_amount = Math.round(discountAmt * 100) / 100;
      newItems[index].tax_amount = Math.round(taxAmt * 100) / 100;
      newItems[index].amount = Math.round((taxableAmount + taxAmt) * 100) / 100;

      return { ...prev, items: newItems };
    });
  };

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.rate || 0)), 0);
  const lineDiscountTotal = formData.items.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const taxableAmount = subtotal - lineDiscountTotal;
  const taxTotal = formData.items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const beforeRoundOff = taxableAmount + taxTotal + (parseFloat(formData.shipping_charges) || 0) + (parseFloat(formData.adjustment) || 0);
  const roundOff = formData.enable_round_off ? Math.round(beforeRoundOff) - beforeRoundOff : 0;
  const grandTotal = beforeRoundOff + roundOff;

  const handleSave = async (asDraft = false) => {
    if (!formData.customer_id) { toast.error('Please select a customer'); return; }
    if (formData.items.length === 0) { toast.error('Please add at least one item'); return; }

    setSaving(true);
    try {
      const customer = customers.find(c => (c.party_id || c.id) === formData.customer_id);
      const payload = {
        ...formData,
        customer_name: customer?.name || '',
        customer_email: customer?.email || '',
        customer_phone: customer?.mobile || customer?.phone || '',
        customer_gstin: customer?.gstin || '',
        billing_address: customer?.billing_address || customer?.address || '',
        save_as_draft: asDraft,
        subtotal, discount_total: lineDiscountTotal, taxable_amount: taxableAmount,
        tax_total: taxTotal, grand_total: grandTotal, round_off_amount: roundOff
      };

      const response = isEditing
        ? await api.put(`/proforma-invoices/${proformaId}`, payload)
        : await api.post('/proforma-invoices', payload);

      const data = response.data;
      toast.success(asDraft ? 'Saved as draft' : 'Proforma created');
      navigate(`/business/${bid}/proforma-invoices/${data.proforma?.id || proformaId}`);
    } catch (error) {
      // Route through getApiErrorMessage so Pydantic 422 array responses
      // don't crash React with "Objects are not valid as a React child".
      toast.error(getApiErrorMessage(error, 'Error saving proforma'));
    }
    finally { setSaving(false); }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile?.includes(customerSearch) ||
    c.email?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    p.code?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* ══════════════════════════════════════════════════════════════════════
          PREMIUM HEADER - Commercial Grade Design
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* LEFT: Back Button + Title */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/business/${bid}/proforma-invoices`)}
                className="group flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <h1 className="text-[18px] font-semibold text-slate-800">
                  {isEditing ? 'Edit Proforma Invoice' : 'New Proforma Invoice'}
                </h1>
                <p className="text-[12px] text-slate-500 mt-0.5">Pre-sale quotation before tax invoice</p>
              </div>
            </div>
            
            {/* RIGHT: Action Buttons */}
            <div className="flex items-center gap-2">
              <button onClick={() => handleSave(true)} disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
              >
                <FileText className="w-4 h-4" /> Save Draft
              </button>
              <button onClick={() => handleSave(false)} disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-emerald-600/20 active:scale-[0.98]"
              >
                <Check className="w-4 h-4" /> {saving ? 'Saving...' : 'Save & Send'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MAIN CONTENT AREA - Premium Layout
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - 8 cols */}
            <div className="col-span-12 lg:col-span-8 space-y-4">
              
              {/* CUSTOMER DETAILS Card - Premium Style */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <Search className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Customer Details</h2>
                    <p className="text-[11px] text-slate-500">Search and select customer</p>
                  </div>
                </div>
                
                <div className="relative" ref={customerSearchRef}>
                  <label className="block text-[12px] font-medium mb-2 text-slate-600">Customer</label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" value={customerSearch}
                      onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                      onFocus={() => setShowCustomerDropdown(true)}
                      className="w-full pl-11 pr-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-[14px] focus:bg-white focus:border-blue-500 focus:ring-0 focus:outline-none transition-all duration-200"
                      placeholder="Search by name, phone, or email..."
                    />
                  </div>
                  
                  {showCustomerDropdown && filteredCustomers.length > 0 && (
                    <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                      {filteredCustomers.slice(0, 8).map((customer, idx) => {
                        const contact = [customer.mobile, customer.email].filter(Boolean).join(' • ');
                        return (
                          <div key={customer.party_id || customer.id}
                            onClick={() => handleCustomerSelect(customer)}
                            className={`px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150 ${idx !== 0 ? 'border-t border-slate-100' : ''}`}>
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[14px] font-medium text-slate-800 truncate">{customer.name}</p>
                              {contact && <p className="text-[12px] text-slate-500 shrink-0 truncate">{contact}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selectedCustomer && (() => {
                  const selContact = [selectedCustomer.mobile, selectedCustomer.email].filter(Boolean).join(' • ');
                  return (
                    <div className="mt-4 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <p className="text-[13px] font-medium text-slate-800 truncate">{selectedCustomer.name}</p>
                          {selContact && <p className="text-[11px] text-slate-500 truncate">{selContact}</p>}
                        </div>
                        <button onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); setFormData(prev => ({ ...prev, customer_id: '' })); }}
                          className="text-slate-400 hover:text-slate-600 shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* ITEMS Card - Premium Design */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                      <Receipt className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Proforma Items</h2>
                      <p className="text-[11px] text-slate-500">{formData.items.length} item{formData.items.length !== 1 ? 's' : ''} added</p>
                    </div>
                  </div>
                </div>

                {/* Quick Add Row - Premium */}
                <div className="flex items-center gap-3 mb-5 p-3 bg-slate-50 rounded-xl">
                  <div className="flex-1 relative" ref={itemSearchRef}>
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" value={itemSearch}
                      onChange={(e) => { setItemSearch(e.target.value); setShowItemDropdown(true); }}
                      onFocus={() => setShowItemDropdown(true)}
                      className="w-full pl-11 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-[14px] focus:border-purple-500 focus:ring-0 focus:outline-none transition-all duration-200"
                      placeholder="Search items by name or code..."
                    />
                    {showItemDropdown && filteredProducts.length > 0 && (
                      <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                        {filteredProducts.slice(0, 8).map(product => (
                          <div key={product.id} onClick={() => addItem(product)}
                            className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-[13px] font-medium text-slate-800 truncate">{product.name}</p>
                              {product.code && <p className="text-[11px] text-slate-500 shrink-0">{product.code}</p>}
                            </div>
                          </div>
                        ))}
                        {filteredProducts.length > 8 && (
                          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 text-center">
                            Showing 8 of {filteredProducts.length} — type to narrow
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setShowScanner(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-[12px] font-medium">
                    <ScanBarcode className="w-4 h-4" /> Scan
                  </button>
                  <button onClick={() => addItem()}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 text-[12px] font-medium">
                    <Plus className="w-4 h-4" /> Add Line
                  </button>
                </div>

                {/* Items Table */}
                {formData.items.length === 0 ? (
                  <div className="text-center py-10 border border-dashed border-slate-200 rounded-lg bg-slate-50/30">
                    <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-[13px] text-slate-500">No items added yet</p>
                    <p className="text-[12px] text-slate-400 mt-1">Click &quot;Add Line&quot; or search for items to get started</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase">#</th>
                          <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase">Item</th>
                          <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase">HSN/SAC</th>
                          <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-slate-500 uppercase">Qty</th>
                          <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-slate-500 uppercase">Unit</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-slate-500 uppercase">Unit Price</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-slate-500 uppercase">Discount</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-slate-500 uppercase">Tax Rate</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-slate-500 uppercase">Amount</th>
                          <th className="px-3 py-2.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => (
                          <tr key={index} className="border-b border-slate-100 hover:bg-slate-50/50">
                            <td className="px-3 py-2.5 text-[12px] text-slate-500">{index + 1}</td>
                            <td className="px-3 py-2.5">
                              <select value={item.item_id} onChange={(e) => updateItem(index, 'item_id', e.target.value)}
                                className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] focus:outline-none focus:border-blue-500">
                                <option value="">Select Item</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </select>
                            </td>
                            <td className="px-3 py-2.5">
                              <input type="text" value={item.hsn_code || ''} onChange={(e) => updateItem(index, 'hsn_code', e.target.value)}
                                className="w-20 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-center focus:outline-none focus:border-blue-500" placeholder="—" />
                            </td>
                            <td className="px-3 py-2.5">
                              <input type="number" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-center focus:outline-none focus:border-blue-500" min="0" />
                            </td>
                            <td className="px-3 py-2.5">
                              <input type="text" value={item.unit} onChange={(e) => updateItem(index, 'unit', e.target.value)}
                                className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-center focus:outline-none focus:border-blue-500" />
                            </td>
                            <td className="px-3 py-2.5">
                              <input type="number" value={item.rate} onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                                className="w-24 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-right focus:outline-none focus:border-blue-500" min="0" />
                            </td>
                            <td className="px-3 py-2.5">
                              <input type="number" value={item.discount_percent} onChange={(e) => updateItem(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                                className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-right focus:outline-none focus:border-blue-500" min="0" max="100" placeholder="%" />
                            </td>
                            <td className="px-3 py-2.5">
                              <input type="number" value={item.tax_rate} onChange={(e) => updateItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                                className="w-16 px-2 py-1.5 bg-white border border-slate-200 rounded text-[12px] text-right focus:outline-none focus:border-blue-500" min="0" placeholder="%" />
                            </td>
                            <td className="px-3 py-2.5 text-right text-[13px] font-medium text-slate-700">
                              ₹{(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2.5">
                              <button onClick={() => removeItem(index)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* TERMS & NOTES Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide mb-4">Terms & Notes</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Customer Notes (visible on proforma)</label>
                    <textarea value={formData.customer_notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_notes: e.target.value }))}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none resize-none"
                      rows={3} placeholder="Add notes for customer..."
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Terms & Conditions</label>
                    <textarea value={formData.terms_and_conditions}
                      onChange={(e) => setFormData(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none resize-none"
                      rows={3} placeholder="Terms and conditions..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - 4 cols */}
            <div className="col-span-4 space-y-5">
              
              {/* PROFORMA DETAILS Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Proforma Details</h2>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[12px] text-slate-500 mb-1.5">Proforma Date</label>
                      <input type="date" value={formData.proforma_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, proforma_date: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] text-slate-500 mb-1.5">Expiry Date *</label>
                      <input type="date" value={formData.expiry_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Payment Terms</label>
                    <select value={formData.payment_terms}
                      onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none">
                      <option value="due_on_receipt">Due on Receipt</option>
                      <option value="net_7">Net 7</option>
                      <option value="net_15">Net 15</option>
                      <option value="net_30">Net 30</option>
                      <option value="net_45">Net 45</option>
                      <option value="net_60">Net 60</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Reference / PO Number</label>
                    <input type="text" value={formData.reference_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      placeholder="Customer PO or reference"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] text-slate-500 mb-1.5">Salesperson</label>
                    <input type="text" value={formData.salesperson}
                      onChange={(e) => setFormData(prev => ({ ...prev, salesperson: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-blue-500"
                      placeholder="Salesperson name"
                    />
                  </div>
                </div>
              </div>

              {/* SUMMARY Card - Yellow Background */}
              <div className="bg-amber-50 rounded-xl p-5 shadow-sm border border-amber-200/60">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Receipt className="w-4 h-4 text-amber-700" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wide">Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  {lineDiscountTotal > 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-600">Discount</span>
                      <span className="text-red-600">- ₹{lineDiscountTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-emerald-600">+ ₹{taxTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  <div className="border-t border-amber-200 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-800">Grand Total</span>
                      <span className="text-[18px] font-bold text-emerald-600">
                        ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 mt-3 cursor-pointer">
                    <input type="checkbox" checked={formData.enable_round_off}
                      onChange={(e) => setFormData(prev => ({ ...prev, enable_round_off: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-[12px] text-slate-600">Enable round off</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barcode Scanner Modal */}
      <BarcodeScannerModal isOpen={showScanner} onClose={() => setShowScanner(false)}
        onScan={(product) => addItem(product)} products={products}
      />
    </div>
  );
};

export default CreateProformaInvoice;
